import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { retrieveClaimTypesQueryOptions } from "@/lib/data/react-query/matters/claim-type";
import {
  FilterSelector,
  type FilterOption,
} from "@/components/ui/FilterSelector/FilterSelector";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { AsyncSelectOptions } from "@/components/ui/FormFields/AsyncSelectOptions";
import { FilterBar } from "@/components/ui/FilterBar/FilterBar";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { type MatterStatus, type IncidentType } from "@/lib/types";
import {
  ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS,
  getMatterStatusCategoryLabel,
} from "@/lib/data/static/matterStatics";
import { formatUsersName } from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { sortBy } from "lodash";
import { Check } from "lucide-react";
import { useMemo, useState } from "react";
import { CountryRegionData } from "react-country-region-selector";
import toast from "react-hot-toast";

const UNASSIGNED_ASSIGNEE_FILTER_VALUE = "__unassigned_assignee__";
const DEFAULT_INCIDENT_COUNTRY_CODE = "US";

const incidentStateOptions = CountryRegionData.default
  .filter(([, countryCode]) => countryCode === DEFAULT_INCIDENT_COUNTRY_CODE)
  .flatMap(([, , regions]) =>
    regions.split("|").map((region) => {
      const [label] = region.split("~");
      return { label, value: label };
    }),
  )
  .filter(
    (option, index, options) =>
      options.findIndex(({ value }) => value === option.value) === index,
  )
  .sort((a, b) => a.label.localeCompare(b.label));

type QuickMatterFilterProps = {
  filterOption: FilterOption;
  selectedValues: string[];
  onAddFilter: (key: string, value: string) => void;
  onRemoveFilter: (key: string, value: string) => void;
};

function QuickMatterFilter({
  filterOption,
  selectedValues,
  onAddFilter,
  onRemoveFilter,
}: QuickMatterFilterProps) {
  const [open, setOpen] = useState(false);
  const input = filterOption.input;
  const items =
    input.type === "select" || input.type === "searchable-select"
      ? input.items
      : [];
  const isSearchable = input.type === "searchable-select";
  const asyncInput = isSearchable && input.onSearchChange ? input : undefined;
  const visibleItems = items.filter((item) => {
    if (asyncInput?.isItemsLoading || asyncInput?.isItemsError) return false;
    if (!asyncInput?.search?.trim()) return true;
    return (
      asyncInput.matchingValues?.includes(item.value) ||
      item.label.toLowerCase().includes(asyncInput.search.trim().toLowerCase())
    );
  });
  const groupedItems = Array.from(
    visibleItems.reduce((groups, item) => {
      const group = item.group ?? "";
      const groupItems = groups.get(group) ?? [];
      groupItems.push(item);
      groups.set(group, groupItems);
      return groups;
    }, new Map<string, typeof visibleItems>()),
  );
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    asyncInput?.onOpenChange?.(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-8 rounded-full px-3 text-xs font-medium"
        >
          {filterOption.label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command shouldFilter={!asyncInput}>
          {isSearchable ? (
            <CommandInput
              value={asyncInput?.search}
              onValueChange={asyncInput?.onSearchChange}
              placeholder={`Search ${filterOption.label.toLowerCase()}...`}
            />
          ) : null}
          <CommandList>
            {!asyncInput?.isItemsLoading && !asyncInput?.isItemsError ? (
              <CommandEmpty>No options found.</CommandEmpty>
            ) : null}
            {groupedItems.map(([group, groupItems]) => {
              const content = groupItems.map((item) => {
                const isSelected = selectedValues.includes(item.value);
                return (
                  <CommandItem
                    key={item.value}
                    value={`${item.label} ${item.value}`}
                    onSelect={() => {
                      if (isSelected) {
                        onRemoveFilter(filterOption.value, item.value);
                      } else {
                        onAddFilter(filterOption.value, item.value);
                      }
                      handleOpenChange(false);
                    }}
                  >
                    <Check
                      className={isSelected ? "opacity-100" : "opacity-0"}
                    />
                    {item.label}
                  </CommandItem>
                );
              });

              return group ? (
                <CommandGroup key={group} heading={group}>
                  {content}
                </CommandGroup>
              ) : (
                content
              );
            })}
            {asyncInput ? <AsyncSelectOptions {...asyncInput} /> : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface MattersSearchBarProps {
  searchObject: Partial<Record<string, string[]>>;
  addSearchObjectValue: (key: string, value: string, replace?: boolean) => void;
  removeSearchObjectValue: (key: string, value: string) => void;
  clearSearchObject?: () => void | Promise<void>;
  showAssigneeFilter?: boolean;
  showReferredByFilter?: boolean;
  showReferredToFilter?: boolean;
  showStatusStartedFilter?: boolean;
  statusCategories?: string[];
  showHelperText?: boolean;
  selectStyle?: string | null;
  containerClassName?: string;
  fullWidth?: boolean;
  layout?: "selector" | "list" | "quick" | "bar";
}

const MattersSearchBar = ({
  searchObject,
  addSearchObjectValue,
  removeSearchObjectValue,
  clearSearchObject,
  showAssigneeFilter = true,
  showReferredByFilter = false,
  showReferredToFilter = false,
  showStatusStartedFilter = false,
  statusCategories = [...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS],
  showHelperText = false,
  selectStyle = "mb-[-8px]",
  containerClassName,
  fullWidth = false,
  layout = "bar",
}: MattersSearchBarProps) => {
  const selectedFilters = useMemo(() => {
    const { unassigned, ...filters } = searchObject;

    if (!unassigned?.includes("true")) {
      return filters;
    }

    return {
      ...filters,
      userId: [...(filters.userId ?? []), UNASSIGNED_ASSIGNEE_FILTER_VALUE],
    };
  }, [searchObject]);

  const { data: claimTypeData } = useQuery(retrieveClaimTypesQueryOptions());
  const hasIncidentTypesPermission = Boolean(true);
  const hasMatterStatusesPermission = Boolean(true);
  const hasUsersPermission = Boolean(true);
  const hasMatterTagsPermission = Boolean(true);

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const userDirectory = useUserDirectory({
    enabled: hasUsersPermission && showAssigneeFilter,
    selectedIds: searchObject.userId ?? [],
  });

  const teams = useMemo(
    () => matterSettingsData?.data.teams ?? [],
    [matterSettingsData],
  );

  const filterOptions = useMemo<FilterOption[]>(() => {
    const options: FilterOption[] = [];

    const canViewMatterDetails = Boolean(true);

    if (canViewMatterDetails) {
      const clientEmailFilterInput: FilterOption["input"] = {
        type: "text",
        placeholder: "Search client email",
      };

      const clientPhoneFilterInput: FilterOption["input"] = {
        type: "text",
        placeholder: "Search client phone",
      };

      options.push({
        label: "Title",
        value: "title",
        input: {
          type: "text",
          placeholder: "Search title",
        },
      });

      options.push({
        label: "Client",
        value: "contactName",
        input: {
          type: "text",
          placeholder: "Search client name",
        },
      });

      options.push({
        label: "Client email",
        value: "clientEmail",
        input: clientEmailFilterInput,
      });

      options.push({
        label: "Client phone",
        value: "clientPhone",
        input: clientPhoneFilterInput,
      });

      options.push({
        label: "Client birthdate",
        value: "clientDateOfBirthExact",
        input: {
          type: "date",
          placeholder: "Select a client birthdate",
          conditionPlaceholder: "Select condition",
          conditionOptions: [
            { label: "Before", value: "clientDateOfBirthBefore" },
            { label: "After", value: "clientDateOfBirthAfter" },
            { label: "Exact", value: "clientDateOfBirthExact" },
          ],
        },
      });

      options.push({
        label: "Date of loss",
        value: "dateOfLossExact",
        input: {
          type: "date",
          placeholder: "Select a date of loss",
          conditionPlaceholder: "Select condition",
          conditionOptions: [
            { label: "Before", value: "dateOfLossBefore" },
            { label: "After", value: "dateOfLossAfter" },
            { label: "Exact", value: "dateOfLossExact" },
          ],
        },
      });

      options.push({
        label: "Incident city",
        value: "incidentAddressCity",
        input: {
          type: "text",
          placeholder: "Search incident city",
        },
      });

      options.push({
        label: "Incident state",
        value: "incidentAddressState",
        input: {
          type: "searchable-select",
          placeholder: "Select an incident state",
          items: incidentStateOptions,
          emptyMessage: "No states available.",
        },
      });

      options.push({
        label: "Defendant(s)",
        value: "defendantName",
        input: {
          type: "text",
          placeholder: "Search defendant name",
        },
      });

      if (showReferredByFilter) {
        options.push({
          label: "Referred by",
          value: "referredByName",
          input: {
            type: "text",
            placeholder: "Search referred by",
          },
        });
      }

      if (showReferredToFilter) {
        options.push({
          label: "Referred to",
          value: "referredToName",
          input: {
            type: "text",
            placeholder: "Search referred to",
          },
        });
      }

      options.push({
        label: "Insurance claim number",
        value: "insuranceClaimNumber",
        input: {
          type: "text",
          placeholder: "Search claim number",
        },
      });

      options.push({
        label: "Insurance policy number",
        value: "insurancePolicyNumber",
        input: {
          type: "text",
          placeholder: "Search policy number",
        },
      });

      options.push({
        label: "Team",
        value: "team",
        input: {
          type: "searchable-select",
          placeholder: "Select a team",
          items: teams.map((team) => ({
            label: team.label,
            value: team.label,
          })),
          emptyMessage: "No teams available.",
        },
      });
    }

    if (hasMatterTagsPermission) {
      options.push({
        label: "Tag",
        value: "tag",
        input: {
          type: "searchable-select",
          placeholder: "Select a tag",
          items:
            matterSettingsData?.data.tags.map((tag) => ({
              label: tag.label,
              value: tag.label,
            })) ?? [],
          emptyMessage: "No tags available.",
        },
      });
    }

    options.push({
      label: "Claim type",
      value: "claimTypeIds",
      input: {
        type: "searchable-select",
        placeholder: "Select a claim type",
        items: (claimTypeData?.data ?? []).map((claimType) => ({
          label: claimType.label,
          value: claimType.id,
        })),
        emptyMessage: "No claim types available.",
      },
    });
    if (hasIncidentTypesPermission) {
      options.push({
        label: "Incident type",
        value: "incidentTypeId",
        input: {
          type: "searchable-select",
          placeholder: "Select a type",
          items:
            matterSettingsData?.data.incidentTypes?.map(
              (type: IncidentType) => ({
                label: getIncidentTypeLabel(type),
                value: type.id,
              }),
            ) ?? [],
          emptyMessage: "No types available.",
        },
      });
    }

    if (hasMatterStatusesPermission) {
      const applicableStatuses = statusCategories.length
        ? (matterSettingsData?.data.statuses.filter((status) =>
            statusCategories.includes(status.categoryId),
          ) ?? [])
        : (matterSettingsData?.data.statuses ?? []);

      const statusItems = applicableStatuses.map((status: MatterStatus) => ({
        group: getMatterStatusCategoryLabel(status.categoryId),
        label: status.label,
        value: status.id,
      }));

      const statusFilterInput: FilterOption["input"] =
        statusItems.length > 0
          ? {
              type: "searchable-select",
              placeholder: "Select a status",
              items: statusItems,
              emptyMessage: "No statuses available.",
            }
          : {
              type: "select",
              placeholder: "Select a status",
              items: [],
            };

      options.push({
        label: "Status",
        value: "statusId",
        input: statusFilterInput,
      });
    }

    if (showStatusStartedFilter) {
      options.push({
        label: "Dropped date",
        value: "statusStartedFrom",
        input: {
          type: "date",
          placeholder: "Select a dropped date",
          conditionPlaceholder: "Select range boundary",
          conditionOptions: [
            { label: "From", value: "statusStartedFrom" },
            { label: "To", value: "statusStartedTo" },
          ],
        },
      });
    }

    if (showAssigneeFilter && hasUsersPermission) {
      options.push({
        label: "Assignees",
        value: "userId",
        input: {
          ...userDirectory.selectProps,
          type: "searchable-select",
          placeholder: "Select an assignee",
          items: sortBy(
            [
              {
                label: "Unassigned",
                value: UNASSIGNED_ASSIGNEE_FILTER_VALUE,
              },
              ...userDirectory.users.map((user) => ({
                label: formatUsersName(user) ?? user.id,
                value: user.id,
              })),
            ],
            "label",
          ),
          emptyMessage: "No users available.",
        },
      });
    }

    const sortedOptions = sortBy(options, "label");

    // Ensure "Client" is always the first option
    const clientIndex = sortedOptions.findIndex(
      (option) => option.value === "contactName",
    );

    if (clientIndex > -1) {
      const [clientOption] = sortedOptions.splice(clientIndex, 1);
      return [clientOption, ...sortedOptions];
    }

    return sortedOptions;
  }, [
    hasIncidentTypesPermission,
    claimTypeData?.data,
    hasMatterStatusesPermission,
    hasMatterTagsPermission,
    hasUsersPermission,
    matterSettingsData,
    statusCategories,
    teams,
    userDirectory.users,
    userDirectory.selectProps,
    showAssigneeFilter,
    showReferredByFilter,
    showReferredToFilter,
    showStatusStartedFilter,
  ]);

  const handleAddFilter = (key: string, value: string) => {
    const statusStartedFrom = selectedFilters.statusStartedFrom?.[0];
    const statusStartedTo = selectedFilters.statusStartedTo?.[0];
    const isReversedStatusRange =
      (key === "statusStartedFrom" &&
        statusStartedTo &&
        value > statusStartedTo) ||
      (key === "statusStartedTo" &&
        statusStartedFrom &&
        value < statusStartedFrom);

    if (isReversedStatusRange) {
      toast.error("Dropped date From must be on or before To.");
      return;
    }

    if (key === "userId" && value === UNASSIGNED_ASSIGNEE_FILTER_VALUE) {
      addSearchObjectValue("unassigned", "true");
      return;
    }
    const isDateCondition = filterOptions.some(
      (option) =>
        option.input.type === "date" &&
        option.input.conditionOptions?.some(
          (condition) => condition.value === key,
        ),
    );
    addSearchObjectValue(key, value, isDateCondition);
  };

  const handleRemoveFilter = (key: string, value: string) => {
    if (key === "userId" && value === UNASSIGNED_ASSIGNEE_FILTER_VALUE) {
      removeSearchObjectValue("unassigned", "true");
      return;
    }
    removeSearchObjectValue(key, value);
  };

  if (layout === "list") {
    const hasActiveFilters = Object.values(selectedFilters).some(
      (values) => (values?.length ?? 0) > 0,
    );

    return (
      <div className="space-y-5">
        {hasActiveFilters ? (
          <FilterSelector
            filterOptions={filterOptions}
            selectedFilters={selectedFilters}
            onAddFilter={handleAddFilter}
            onRemoveFilter={handleRemoveFilter}
            badgesOnly
          />
        ) : null}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {filterOptions.map((filterOption) => {
            const values = selectedFilters[filterOption.value] ?? [];

            return (
              <FilterSelector
                key={filterOption.value}
                filterOptions={[filterOption]}
                selectedFilters={
                  values.length > 0 ? { [filterOption.value]: values } : {}
                }
                onAddFilter={handleAddFilter}
                onRemoveFilter={handleRemoveFilter}
                showHelperText={false}
                selectStyle={null}
                fullWidth
                hidePrimarySelect
                showActiveFilterBadges={false}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (layout === "quick") {
    const quickFilterValues = [
      "userId",
      "incidentAddressState",
      "statusId",
      "incidentTypeId",
      "claimTypeIds",
      "tag",
    ];
    const quickFilterOptions = quickFilterValues
      .map((value) => filterOptions.find((option) => option.value === value))
      .filter((option): option is FilterOption => option !== undefined);

    return (
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {quickFilterOptions.map((filterOption) => {
          const values = selectedFilters[filterOption.value] ?? [];

          return (
            <QuickMatterFilter
              key={filterOption.value}
              filterOption={filterOption}
              selectedValues={values}
              onAddFilter={handleAddFilter}
              onRemoveFilter={handleRemoveFilter}
            />
          );
        })}
      </div>
    );
  }

  if (layout === "bar") {
    return (
      <FilterBar
        filterOptions={filterOptions}
        selectedFilters={selectedFilters}
        onAddFilter={handleAddFilter}
        onRemoveFilter={handleRemoveFilter}
        onClearFilters={clearSearchObject}
        quickFilterValues={[
          "userId",
          "incidentAddressState",
          "statusId",
          "incidentTypeId",
          "claimTypeIds",
          "tag",
        ]}
      />
    );
  }

  return (
    <FilterSelector
      filterOptions={filterOptions}
      selectedFilters={selectedFilters}
      onAddFilter={handleAddFilter}
      onRemoveFilter={handleRemoveFilter}
      showHelperText={showHelperText}
      selectStyle={selectStyle}
      containerClassName={containerClassName}
      fullWidth={fullWidth}
    />
  );
};

export default MattersSearchBar;
